import { useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Text,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Button,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';

import {
  apiGetQuoteGroupSummary,
  apiGetAutoQuoteSummary,
  apiGetPropertyQuoteSummary,
  apiCreateGroupPolicy,
} from 'api/quote-marketplace';
import type {
  QuoteSummaryData,
  PropertyQuoteSummaryData,
  QuoteSummaryVehicle,
  QuoteSummaryLocation,
} from 'api/quote-marketplace';
import { LOB_PERSONAL_AUTO } from 'variables/constants';
import {
  QuoteBanner,
  QuoteCarrierHeader,
  VehicleSummarySection,
  VehicleQuoteCard,
  RatesSection,
  MessagesSection,
  PremiumSummarySection,
  LocationSummarySection,
  LocationQuoteCard,
  PolicyCoverageSection,
  CombinedPremiumSummary,
  ProceedToApplicationModal,
} from '../components/quote';
import { useQuoteLock, buildPolicyUrl } from '../hooks';
import {
  getVehiclesParsedData,
  type ParsedVehicleData,
} from '../utils/parseVehicleHtml';
import {
  getLocationsParsedData,
  type ParsedLocationData,
} from '../utils/parseLocationHtml';

interface CombinedQuoteStepProps {
  groupId: number;
}

interface VehicleWithParsedData {
  vehicle: QuoteSummaryVehicle;
  parsedData: ParsedVehicleData;
}

interface LocationWithParsedData {
  location: QuoteSummaryLocation;
  parsedData: ParsedLocationData;
}

interface AutoQuoteSectionProps {
  quoteId: number;
}

const AutoQuoteSection = ({ quoteId }: AutoQuoteSectionProps) => {
  const { data, isLoading, error } = useQuery<QuoteSummaryData>({
    queryKey: ['auto-quote-summary', quoteId],
    queryFn: () => apiGetAutoQuoteSummary(quoteId),
    staleTime: 30 * 1000,
  });

  const vehiclesWithParsedData: VehicleWithParsedData[] = useMemo(() => {
    const vehicles = data?.vehicles ?? [];
    const parsedData = getVehiclesParsedData(
      vehicles,
      data?.selected_carrier?.coverage_breakdown_html ?? null,
      data?.selected_carrier?.discounts_html ?? null,
      data?.selected_carrier?.rating_factors_html ?? null,
    );
    return vehicles.map((vehicle: QuoteSummaryVehicle, index: number) => ({
      vehicle,
      parsedData: parsedData[index],
    }));
  }, [
    data?.vehicles,
    data?.selected_carrier?.coverage_breakdown_html,
    data?.selected_carrier?.discounts_html,
    data?.selected_carrier?.rating_factors_html,
  ]);

  if (isLoading) {
    return (
      <QuoteBanner title={`Auto Quote #${quoteId}`} defaultExpanded>
        <Flex justify="center" py={4}>
          <Spinner size="md" />
        </Flex>
      </QuoteBanner>
    );
  }

  if (error || !data) {
    return (
      <QuoteBanner title={`Auto Quote #${quoteId}`} defaultExpanded>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Failed to load quote details
        </Alert>
      </QuoteBanner>
    );
  }

  const { selected_carrier, effective_date } = data;

  if (!selected_carrier) {
    return (
      <QuoteBanner title={`Auto Quote #${quoteId}`} defaultExpanded={false}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          No carrier selected for this quote
        </Alert>
      </QuoteBanner>
    );
  }

  return (
    <Box mb={4}>
      <QuoteCarrierHeader
        carrierName={selected_carrier.carrier_name}
        quoteRef={selected_carrier.acturis_quote_ref}
      />

      <QuoteBanner title={`Auto Quote #${quoteId}`} defaultExpanded>
        <VehicleSummarySection
          vehicles={vehiclesWithParsedData}
          annualPremium={selected_carrier.annual_premium}
          taxes={selected_carrier.taxes}
        />

        {vehiclesWithParsedData.map(({ vehicle, parsedData }, index) => (
          <VehicleQuoteCard
            key={vehicle.id}
            vehicle={vehicle}
            parsedData={parsedData}
            vehicleIndex={index}
          />
        ))}

        <RatesSection
          effectiveDate={effective_date}
          validUntilDate={selected_carrier.valid_until_date}
          riskInfo={selected_carrier.risk_info}
        />

        <MessagesSection ratingNotes={selected_carrier.rating_notes} />

        <PremiumSummarySection
          annualPremium={selected_carrier.annual_premium}
          taxes={selected_carrier.taxes}
        />
      </QuoteBanner>
    </Box>
  );
};

interface PropertyQuoteSectionProps {
  quoteId: number;
}

const PropertyQuoteSection = ({ quoteId }: PropertyQuoteSectionProps) => {
  const { data, isLoading, error } = useQuery<PropertyQuoteSummaryData>({
    queryKey: ['property-quote-summary', quoteId],
    queryFn: () => apiGetPropertyQuoteSummary(quoteId),
    staleTime: 30 * 1000,
  });

  const locationsWithParsedData: LocationWithParsedData[] = useMemo(() => {
    const locations = data?.locations ?? [];
    const parsedData = getLocationsParsedData(
      locations,
      data?.selected_carrier?.coverage_breakdown_html ?? null,
      data?.selected_carrier?.discounts_html ?? null,
    );
    return locations.map((location: QuoteSummaryLocation, index: number) => ({
      location,
      parsedData: parsedData[index],
    }));
  }, [
    data?.locations,
    data?.selected_carrier?.coverage_breakdown_html,
    data?.selected_carrier?.discounts_html,
  ]);

  if (isLoading) {
    return (
      <QuoteBanner title={`Home Quote #${quoteId}`} defaultExpanded>
        <Flex justify="center" py={4}>
          <Spinner size="md" />
        </Flex>
      </QuoteBanner>
    );
  }

  if (error || !data) {
    return (
      <QuoteBanner title={`Home Quote #${quoteId}`} defaultExpanded>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Failed to load quote details
        </Alert>
      </QuoteBanner>
    );
  }

  const { selected_carrier } = data;

  if (!selected_carrier) {
    return (
      <QuoteBanner title={`Home Quote #${quoteId}`} defaultExpanded={false}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          No carrier selected for this quote
        </Alert>
      </QuoteBanner>
    );
  }

  return (
    <Box mb={4}>
      <QuoteCarrierHeader
        carrierName={selected_carrier.carrier_name}
        quoteRef={selected_carrier.acturis_quote_ref}
      />

      <QuoteBanner title={`Home Quote #${quoteId}`} defaultExpanded>
        <LocationSummarySection
          locations={data.locations}
          annualPremium={selected_carrier.annual_premium}
          policyPremium={selected_carrier.policy_premium}
          taxes={selected_carrier.taxes}
        />

        <PolicyCoverageSection html={selected_carrier.policy_coverage_html} />

        {locationsWithParsedData.map(({ location, parsedData }, index) => (
          <LocationQuoteCard
            key={location.id}
            location={location}
            parsedData={parsedData}
            locationIndex={index}
          />
        ))}

        <MessagesSection ratingNotes={selected_carrier.rating_notes} />

        <PremiumSummarySection
          annualPremium={selected_carrier.annual_premium}
          taxes={selected_carrier.taxes}
          productLabel="Home"
        />
      </QuoteBanner>
    </Box>
  );
};

const CombinedQuoteStep = ({ groupId }: CombinedQuoteStepProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isLocked, policyUrl } = useQuoteLock();
  const {
    isOpen: isProceedModalOpen,
    onOpen: openProceedModal,
    onClose: closeProceedModal,
  } = useDisclosure();

  const {
    data: summary,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['quote-group-summary', groupId],
    queryFn: () => apiGetQuoteGroupSummary(groupId),
    enabled: groupId > 0,
    staleTime: 30 * 1000,
  });

  const proceedMutation = useMutation({
    mutationFn: () => apiCreateGroupPolicy(groupId),
    onSuccess: ({ policy_uuid, submission_id }) => {
      closeProceedModal();
      queryClient.invalidateQueries({ queryKey: ['marketplace-quote'] });
      navigate(buildPolicyUrl(submission_id, policy_uuid, 'validations') ?? '');
    },
    onError: (err: any) => {
      closeProceedModal();
      toast({
        title: 'Failed to proceed to application',
        description: err?.response?.data?.error || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" />
        <Text mt={2} color="gray.500">
          Loading combined quote summary...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        Failed to load combined quote summary
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">No summary data available</Text>
      </Box>
    );
  }

  const hasAnyCarrier = summary.quotes.some((q) => q.has_carrier);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="lg" fontWeight="700">
          Combined Quote Summary
        </Text>
        {summary.all_quotes_have_carrier ? (
          <Badge colorScheme="green" fontSize="sm" px={2} py={1}>
            All Carriers Selected
          </Badge>
        ) : (
          <Badge colorScheme="orange" fontSize="sm" px={2} py={1}>
            Carrier Selection Incomplete
          </Badge>
        )}
      </Flex>

      {summary.quotes.map((quote) =>
        quote.line_of_business === LOB_PERSONAL_AUTO ? (
          <AutoQuoteSection key={quote.id} quoteId={quote.id} />
        ) : (
          <PropertyQuoteSection key={quote.id} quoteId={quote.id} />
        ),
      )}

      {hasAnyCarrier && (
        <CombinedPremiumSummary
          quotes={summary.quotes}
          combinedAnnualPremium={summary.combined_annual_premium}
          combinedMonthlyPremium={summary.combined_monthly_premium}
        />
      )}

      {!hasAnyCarrier && (
        <Alert status="info" borderRadius="md" mt={4}>
          <AlertIcon />
          Select carriers for your quotes to see the combined premium summary.
        </Alert>
      )}

      <Flex justify="flex-end" mt={6}>
        {isLocked ? (
          policyUrl && (
            <Button
              as={RouterLink}
              to={policyUrl}
              rightIcon={<ArrowForwardIcon />}
              colorScheme="brand"
              size="lg"
            >
              View Application
            </Button>
          )
        ) : summary.all_quotes_have_carrier ? (
          <Button
            colorScheme="blue"
            size="lg"
            onClick={openProceedModal}
            isLoading={proceedMutation.isPending}
            loadingText="Proceeding..."
          >
            Proceed to Application
          </Button>
        ) : (
          <Tooltip label="Select carriers for all quotes to proceed" hasArrow>
            <Button
              colorScheme="blue"
              size="lg"
              isDisabled
              _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            >
              Proceed to Application
            </Button>
          </Tooltip>
        )}
      </Flex>

      <ProceedToApplicationModal
        isOpen={isProceedModalOpen}
        onClose={closeProceedModal}
        onConfirm={() => proceedMutation.mutate()}
        isLoading={proceedMutation.isPending}
        isGrouped
      />
    </Box>
  );
};

export default CombinedQuoteStep;
